/**
 * Payments 画面の「直近の購入」と全額返金。購入者は Players 画面と同じドロワーで開く（BAN までその場でできる）。
 *
 * 返金の確認の強さ:
 *   - development: 通常の確認ダイアログ（テストの購入を気軽に戻せる）
 *   - production: 商品キーを打たないと押せない。返金は本物のお金が戻り、付与（ジェム等）も取り消され、
 *     取り消せない。Enter 連打・誤クリックで起きてはいけないので、GitHub のリポジトリ削除と同じ「名前を打つ」確認にする
 */
import { useState } from 'react';
import { api, ApiError, type EnvName, type GrantLine, type PaymentMode, type PurchaseRow, type PurchaseStatus } from '../api';
import { PlayerDrawer } from '../components/PlayerDrawer';
import { ago, ConfirmDialog, fullDate, shortId } from '../components/ui';
import { errorText, formatNumber, translate, useT, type MessageKey } from '../i18n';
import { money } from '../money';

/** 決済モードの表示名（表示中の言語） */
export const modeLabel = (m: PaymentMode) => translate(`pay.mode.${m}`);

/** 状態の色: 支払い済み = ok / 返金 = warn / 失敗・チャージバック = danger / 未払いのまま閉じた = 無色 */
const STATUS_PILL: Record<PurchaseStatus, { label: MessageKey; cls: string }> = {
  completed: { label: 'pay.status.completed', cls: 'pill-ok' },
  pending: { label: 'pay.status.pending', cls: 'pill-accent' },
  refunded: { label: 'pay.status.refunded', cls: 'pill-warn' },
  disputed: { label: 'pay.status.disputed', cls: 'pill-danger' },
  failed: { label: 'pay.status.failed', cls: 'pill-danger' },
  expired: { label: 'pay.status.expired', cls: '' },
  canceled: { label: 'pay.status.canceled', cls: '' }
};

// 付与の行は「数 + config のキー」（キーはコード識別子なので訳さない）
const grantText = (g: GrantLine) => `${formatNumber(g.count)} ${g.currency ?? g.item}`;

export function PurchasesCard({
  base, env, purchases, onRefunded
}: {
  base: string; env: EnvName; purchases: PurchaseRow[]; onRefunded: (message: string) => void;
}) {
  const [player, setPlayer] = useState<string | null>(null);
  const [target, setTarget] = useState<PurchaseRow | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const strong = env === 'production';
  const { t, tr } = useT();
  const confirmed = !strong || (target !== null && typed.trim() === target.productKey);

  const close = () => {
    setTarget(null);
    setTyped('');
    setErr(null);
  };

  const refund = async () => {
    if (!target || !confirmed) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api<{ purchaseId: string; status: string; removed: GrantLine[] }>(`${base}/payments/purchases/${target.id}/refund`, { method: 'POST', body: {} });
      const who = target.playerName ?? (target.playerId ? shortId(target.playerId) : t('pay.deletedPlayer'));
      onRefunded(
        `${t('pay.refunded', { amount: money(target.amount, target.currency), title: target.title })}${t('common.sentenceSep')}` +
          (res.removed.length
            ? t('pay.removed', { items: res.removed.map(grantText).join(t('pay.listSep')), who })
            : t('pay.nothingRemoved', { who }))
      );
      close();
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">{t('pay.recent')}</h2>
        <span className="muted small">{t('pay.recentRange')}</span>
      </div>
      {purchases.length === 0 ? (
        <p className="muted small">{t('pay.noCheckouts', { env: t(`env.inline.${env}`) })}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('pay.col.purchase')}</th>
                <th scope="col">{t('pay.col.player')}</th>
                <th scope="col" className="num">
                  {t('pay.col.amount')}
                </th>
                <th scope="col">{t('pay.col.status')}</th>
                <th scope="col" className="hide-sm">
                  {t('pay.col.when')}
                </th>
                <th scope="col">
                  <span className="sr-only">{t('pay.col.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p) => (
                <tr key={p.id}>
                  <td>
                    <span className="purchase-title">{p.title}</span>
                    <span className="mono muted small">{p.productKey}</span>
                  </td>
                  <td>
                    {p.playerId ? (
                      <button type="button" className="link-btn player-cell" onClick={() => setPlayer(p.playerId)} title={t('pay.openPlayer')}>
                        <span>
                          <span className="player-name">{p.playerName ?? <em className="muted">{t('common.unnamed')}</em>}</span>
                          <span className="mono muted small">{shortId(p.playerId)}</span>
                        </span>
                      </button>
                    ) : (
                      // 削除済みプレイヤー: 購入記録は匿名化して残る（開けるプレイヤー詳細は無い）
                      <em className="muted">{t('pay.deletedPlayer')}</em>
                    )}
                  </td>
                  <td className="num">
                    <span className="mono">{money(p.amount, p.currency)}</span>
                    {p.refundedAmount > 0 && p.status !== 'refunded' && (
                      <span className="small muted"> −{money(p.refundedAmount, p.currency)}</span>
                    )}
                  </td>
                  <td>
                    <span className={`pill ${STATUS_PILL[p.status].cls}`}>{t(STATUS_PILL[p.status].label)}</span>
                    {/* 本番環境に偽決済・テストの購入が混ざっている時だけ印を付ける（dev では全部そうなので出さない） */}
                    {env === 'production' && p.mode !== 'live' && <span className="pill">{modeLabel(p.mode)}</span>}
                  </td>
                  <td className="hide-sm" title={fullDate(p.completedAt ?? p.createdAt)}>
                    {ago(p.completedAt ?? p.createdAt)}
                  </td>
                  <td className="cell-actions">
                    {p.status === 'completed' && (
                      <button type="button" className="btn btn-ghost btn-sm btn-danger-text" onClick={() => setTarget(p)}>
                        {t('pay.refund')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!target}
        title={target ? t('pay.refundTitle', { amount: money(target.amount, target.currency) }) : t('pay.refund')}
        confirmLabel={t('pay.refundConfirm')}
        tone="danger"
        busy={busy}
        confirmDisabled={!confirmed}
        onClose={close}
        onConfirm={() => void refund()}
      >
        {target && (
          <>
            <p>
              {tr('pay.boughtBy', {
                title: <strong>{target.title}</strong>,
                who: <strong>{target.playerName ?? (target.playerId ? shortId(target.playerId) : t('pay.deletedPlayer'))}</strong>,
                ago: ago(target.completedAt ?? target.createdAt)
              })}
            </p>
            <p>
              {t(target.mode === 'live' ? 'pay.refundLive' : target.mode === 'test' ? 'pay.refundTest' : 'pay.refundFake')}
              {t('common.sentenceSep')}
              {t('pay.refundGrants')}
            </p>
            {strong && (
              <label className="field">
                <span>
                  {tr('pay.typeToConfirm', { key: <code>{target.productKey}</code> })}
                </span>
                <input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  aria-invalid={typed.length > 0 && !confirmed}
                />
              </label>
            )}
          </>
        )}
        {err && (
          <p className="alert alert-error" role="alert">
            {err}
          </p>
        )}
      </ConfirmDialog>

      <PlayerDrawer base={`${base}/players`} playerId={player} env={env} onClose={() => setPlayer(null)} onChanged={() => undefined} />
    </section>
  );
}
